import React, { useEffect, useState } from 'react'
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg'
import ModalContainer from '../../common/modal/ModalContainer'
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/arrow-right.svg'
import { useDispatch } from 'react-redux'
import { useFilterOption } from '../../redux_toolkit/V3/Filter/FilterOptionReducers'
import { FilterActionTypes, useFilter } from '../../redux_toolkit/V3/Filter'
import {
    ADD_FILTER_FIELDS,
    CLEAR_FILTER_FIELD,
    CLEAR_FILTER_FIELDS,
    REMOVE_FILTER_FIELD
} from '../../redux_toolkit/@types/action.types'
import { filterTypes } from './filterUtil'
import {
    capitalizeFirstAndLowercaseRest,
    validateNumber
} from '../../helpers/Util'
import PriceRangeModal from './PriceRangeModal'
import { addMixpanelTrackingEvent } from '../../services/CommonService'
import Image from '../../common/Image'

const FilterComponent = ({
    children,
    requiredFields,
    filteredDataLength,
    extraClass
}) => {
    const [activeFilterType, setActiveFilterType] = useState('')
    const [secondOptionList, setSecondOptionList] = useState([])
    const [selectedItem, setSelectedItem] = useState(null)
    const [filterOptions, setFilterOptions] = useState({})
    const [appliedFilterTypes, setAppliedFilterTypes] = useState([])
    const [showFilter, setShowFilter] = useState(false)
    const [showPriceRangeFilter, setShowPriceRangeFilter] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [compositionMinValue, setCompositionMinValue] = useState('')
    const [compositionMaxValue, setCompositionMaxValue] = useState('')
    const [matchingItemList, setMatchingItemList] = useState([])
    const [lastActiveFilterType, setLastActiveFilterType] = useState('')
    const options = useFilterOption()
    const filterStore = useFilter()
    const dispatch = useDispatch()

    const onClickAction = (item, fieldName) => {
        if (!fieldName) return
        let selections = filterOptions[fieldName]
            ? filterOptions[fieldName]
            : []
        let isAlreadyExist = selections.find((obj) => obj.id === item.id)
        if (isAlreadyExist) {
            selections = selections.filter((obj) => obj.id !== item.id)
        } else {
            if (fieldName === 'fabrics') {
                let values = item.constructionList.map((i) => i.name)
                selections.push({
                    id: item.id,
                    value: item.name,
                    constructions: values
                })
            } else {
                selections.push({ id: item.id, value: item.name })
            }
        }
        setFilterOptions((prev) => ({
            ...prev,
            [fieldName]: selections
        }))
    }

    const getFirstMatchingItem = (list, searchTerm) => {
        return list?.find((item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
    }

    useEffect(() => {
        const updateSelectedItemAndSecondOptionList = (
            list,
            secondStepListName
        ) => {
            if (searchTerm) {
                const firstMatchingItem = getFirstMatchingItem(list, searchTerm)
                if (firstMatchingItem) {
                    setSelectedItem(firstMatchingItem)
                    setSecondOptionList(
                        firstMatchingItem[secondStepListName] || []
                    )
                }
            } else {
                setSelectedItem(list?.[0])
                setSecondOptionList(list?.[0]?.[secondStepListName] || [])
            }
        }

        switch (activeFilterType) {
            case filterTypes.CATEGORY:
                updateSelectedItemAndSecondOptionList(
                    options.categoryList,
                    'subCategoryEntityCountList'
                )
                break
            case filterTypes.SEASON:
                setSecondOptionList(options.seasons)
                break
            case filterTypes.AGE_GROUP:
                setSecondOptionList(options.ageGroups)
                break
            case filterTypes.DESIGN_LOOK:
                setSecondOptionList(options.designLooks)
                break
            case filterTypes.FABRIC:
                updateSelectedItemAndSecondOptionList(
                    options.fabricTypeList,
                    'fabricConstructionGroupList'
                )
                break
            case filterTypes.DEPARTMENT:
                setSecondOptionList(options.departments)
                break
            case filterTypes.COMPOSITION:
                if (searchTerm) {
                    const firstMatchingItem = getFirstMatchingItem(
                        options.fiberList,
                        searchTerm
                    )
                    if (firstMatchingItem) {
                        setSelectedItem(firstMatchingItem)
                    }
                } else {
                    setSelectedItem(options.fiberList?.[0])
                }
                break
            default:
                setSecondOptionList([])
                break
        }
    }, [searchTerm, activeFilterType, options])

    const getOptionListStepsNames = () => {
        const optionListNames = {
            firstStepList: 'categoryList',
            secondStepList: 'subCategoryEntityCountList'
        }

        switch (activeFilterType) {
            case filterTypes.COMPOSITION:
                return {
                    firstStepList: 'fiberList',
                    secondStepList: ''
                }
            case filterTypes.FABRIC:
                return {
                    firstStepList: 'fabricTypeList',
                    secondStepList: 'fabricConstructionGroupList'
                }
            default:
                return optionListNames
        }
    }

    const checkIsActiveClass = (item, selectedItem) => {
        if (!item || !selectedItem) return ''

        switch (activeFilterType) {
            case filterTypes.FABRIC:
                return item?.constant === selectedItem?.constant ? 'active' : ''
            case filterTypes.COMPOSITION:
                return item?.name === selectedItem?.name ? 'active' : ''
            default:
                return item?.id === selectedItem?.id ? 'active' : ''
        }
    }

    useEffect(() => {
        if (
            activeFilterType === filterTypes.CATEGORY ||
            activeFilterType === filterTypes.FABRIC ||
            activeFilterType === filterTypes.COMPOSITION
        ) {
            const { firstStepList, secondStepList } = getOptionListStepsNames()
            const fields = options[firstStepList]
            const matchingItems = fields?.filter((item) => {
                const hasMatchingSecondStep = secondStepList
                    ? item?.[secondStepList]?.some((secondStep) => {
                          const searchKey = createSearchKey(
                              item.name,
                              secondStep.name
                          )
                          return searchKey
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                      })
                    : item.name.toLowerCase().includes(searchTerm.toLowerCase())

                return hasMatchingSecondStep
            })

            if (matchingItems?.length !== matchingItemList?.length) {
                setMatchingItemList(matchingItems)
            }
        }
    }, [searchTerm, activeFilterType, options, matchingItemList])

    useEffect(() => {
        if (matchingItemList?.length > 0) {
            setSelectedItem(matchingItemList[0])
            const { secondStepList } = getOptionListStepsNames()
            setSecondOptionList(matchingItemList[0][secondStepList] || [])
        } else {
            setSelectedItem(null)
            setSecondOptionList([])
        }
    }, [matchingItemList])

    const renderFirstOptions = () => {
        return matchingItemList?.map((item, i) => (
            <p
                className={`item regular-14 d-flex justify-between items-center cursor-pointer ${checkIsActiveClass(
                    item,
                    selectedItem
                )}`}
                key={`filter_option_${i}`}
                onClick={() => {
                    setSelectedItem(item)
                    setSecondOptionList(
                        item?.[getOptionListStepsNames().secondStepList] || []
                    )
                }}
            >
                <span className='mr-2'>
                    {capitalizeFirstAndLowercaseRest(item.name)}
                </span>
                {activeFilterType === filterTypes.CATEGORY &&
                    item.id === selectedItem?.id && <ArrowRightIcon />}
            </p>
        ))
    }

    const getFieldName = () => {
        let fieldName
        if (activeFilterType === filterTypes.CATEGORY) {
            fieldName = 'subCategories'
        } else if (activeFilterType === filterTypes.SEASON) {
            fieldName = 'seasons'
        } else if (activeFilterType === filterTypes.AGE_GROUP) {
            fieldName = 'ageGroups'
        } else if (activeFilterType === filterTypes.DESIGN_LOOK) {
            fieldName = 'designLooks'
        } else if (activeFilterType === filterTypes.FABRIC) {
            fieldName = 'fabrics'
        } else if (activeFilterType === filterTypes.DEPARTMENT) {
            fieldName = 'departments'
        } else if (activeFilterType === filterTypes.COMPOSITION) {
            fieldName = 'composition'
        } else if (activeFilterType === filterTypes.PRICE_RANGE) {
            fieldName = 'priceRange'
        }
        return fieldName
    }

    const isCheckboxChecked = (fieldName, item) => {
        if (filterOptions[fieldName]) {
            return (
                filterOptions[fieldName]?.find((obj) => obj.id === item.id) !==
                undefined
            )
        }
        return false
    }

    const isAllCheckboxSelected = () => {
        let fieldName = getFieldName()

        let optionIds = filteredList?.map((item) => item.id) || []

        let selectedOptions = filterOptions[fieldName]?.filter((item) =>
            optionIds.includes(item.id)
        )

        return selectedOptions?.length === optionIds.length
    }

    const createSearchKey = (firstStepName, secondStepName) => {
        return `${firstStepName}_${secondStepName}`
    }

    const filteredList = secondOptionList?.filter((secondStep) => {
        const searchKey = createSearchKey(selectedItem?.name, secondStep.name)
        return searchKey.toLowerCase().includes(searchTerm.toLowerCase())
    })

    const onSetComposition = () => {
        let fieldName = getFieldName()
        let compositionPartDTOList = [...(filterStore?.composition || [])]
        let compositionGroup = {
            fiberName: selectedItem?.name,
            minPercentage: compositionMinValue,
            maxPercentage: compositionMaxValue
        }

        const existingIndex = compositionPartDTOList.findIndex(
            (item) => item.fiberName === compositionGroup.fiberName
        )

        if (existingIndex !== -1) {
            compositionPartDTOList[existingIndex] = compositionGroup
        } else {
            compositionPartDTOList.push(compositionGroup)
        }

        setFilterOptions((prev) => ({
            ...prev,
            [fieldName]: compositionPartDTOList
        }))
    }

    useEffect(() => {
        if (compositionMinValue && compositionMaxValue) {
            onSetComposition()
        }
    }, [compositionMaxValue, compositionMinValue, selectedItem])

    useEffect(() => {
        if (
            filterStore?.mainFabricList ||
            filterStore?.isMoreExplore ||
            filterStore?.categoryList
        ) {
            return
        }

        const filterTypeMap = {
            'New In': 'isNew',
            Sold: 'availabilityStatus',
            'Designer’s Pick': 'designersPick',
            'Design Look': 'designLooks',
            Category: 'subCategories',
            'Price Range': 'priceRange',
            Season: 'seasons',
            Composition: 'composition',
            Fabric: 'fabrics',
            Department: 'departments'
        }

        const filterStoreKey = filterTypeMap[lastActiveFilterType]

        if (filterStoreKey && filterStore[filterStoreKey]) {
            const lastFilteredArray = filterStore[filterStoreKey]

            const requestBody = {
                [lastActiveFilterType]: lastFilteredArray
            }

            addMixpanelTrackingEvent(requestBody, 'BUYER_FILTER_DESIGN_LAB')
        }
    }, [filterStore, lastActiveFilterType])

    const renderSecondSteps = () => {
        let fieldName = getFieldName()
        return filteredList?.map((item, i) => {
            if (!item.isPrimary) {
                return (
                    <div
                        className={`item d-flex justify-between items-center ${
                            isCheckboxChecked(fieldName, item) ? 'selected' : ''
                        }`}
                        key={`second_step_${item.id}`}
                    >
                        <div className='d-flex items-center'>
                            <input
                                type='checkbox'
                                name='categories'
                                id={`category_item_${i}`}
                                value={item.id}
                                checked={isCheckboxChecked(fieldName, item)}
                                onChange={() => onClickAction(item, fieldName)}
                            />
                            <label
                                htmlFor={`category_item_${i}`}
                                className='regular-14 ml-2'
                            >
                                {capitalizeFirstAndLowercaseRest(item.name)}
                            </label>
                        </div>
                    </div>
                )
            }
        })
    }

    const renderSecondStepsForComposition = () => {
        return (
            <div className='single-step single-step-right'>
                <div className='max-w-[220px]'>
                    <input
                        type='number'
                        name='minRange'
                        placeholder='Min %'
                        value={compositionMinValue}
                        onChange={(e) => setCompositionMinValue(e.target.value)}
                        onKeyPress={validateNumber}
                        className='mb-3'
                    />
                    <input
                        type='number'
                        name='maxRange'
                        placeholder='Max %'
                        value={compositionMaxValue}
                        onChange={(e) => setCompositionMaxValue(e.target.value)}
                        onKeyPress={validateNumber}
                    />
                </div>
            </div>
        )
    }

    const onClickAllCheckbox = (e) => {
        let { checked } = e.target
        let fieldName = getFieldName()

        let optionIds = filteredList?.map((item) => item.id) || []

        if (!checked) {
            setFilterOptions((prev) => ({
                ...prev,
                [fieldName]: prev[fieldName]?.filter(
                    (item) => !optionIds.includes(item.id)
                )
            }))
        } else {
            let existingIds =
                filterOptions[fieldName]?.map((item) => item.id) || []

            // Handle selection of filtered items (based on search results)
            let tmpOptions =
                filteredList?.filter(
                    (item) => !existingIds.includes(item.id)
                ) || []

            if (fieldName === 'fabrics') {
                tmpOptions = tmpOptions.map((item) => ({
                    id: item.id,
                    value: item.name,
                    constructions: item.constructionList.map(
                        (construction) => construction.name
                    )
                }))
            } else {
                tmpOptions = tmpOptions.map((item) => ({
                    id: item.id,
                    value: item.name
                }))
            }

            setFilterOptions((prev) => ({
                ...prev,
                [fieldName]: [...(prev[fieldName] || []), ...tmpOptions]
            }))
        }
    }

    const clearState = () => {
        setSelectedItem(null)
        setActiveFilterType('')
        setSecondOptionList([])
        setFilterOptions({})
        setShowFilter(false)
        setCompositionMinValue('')
        setCompositionMaxValue('')
    }

    const onClickApply = () => {
        dispatch(FilterActionTypes[ADD_FILTER_FIELDS]({ data: filterOptions }))
        setLastActiveFilterType(activeFilterType)
        clearState()
        if (showPriceRangeFilter) {
            setShowPriceRangeFilter(false)
        }
    }

    const clearFilter = () => {
        setCompositionMinValue('')
        setCompositionMaxValue('')
        let fieldName = getFieldName()
        setFilterOptions((prev) => ({ ...prev, [fieldName]: undefined }))
        dispatch(FilterActionTypes[CLEAR_FILTER_FIELD](fieldName))
    }

    const renderFilterOptions = () => (
        <div className='common-popup'>
            <div className='common-popup-header d-flex justify-content-between'>
                <div className='popup-tilte'>
                    <h3 className='semibold-16 mb-0'>{activeFilterType}</h3>
                </div>
                <div className='close-btn cursor-pointer' onClick={clearState}>
                    <Image src='/icons/close.svg' alt='close' />
                </div>
            </div>

            <div className='common-popup-body scroll-y-label'>
                <div className='search-box'>
                    <input
                        type='text'
                        onChange={(e) => setSearchTerm(e.target.value)}
                        value={searchTerm}
                        placeholder='Search'
                        autoFocus={true}
                    />
                </div>

                <div className='filter-steps'>
                    {[
                        filterTypes.CATEGORY,
                        filterTypes.FABRIC,
                        filterTypes.COMPOSITION
                    ].includes(activeFilterType) && (
                        <div className='single-step'>
                            {renderFirstOptions()}
                        </div>
                    )}
                    {secondOptionList?.length > 0 && (
                        <div
                            className={`single-step ${
                                [
                                    filterTypes.CATEGORY,
                                    filterTypes.FABRIC,
                                    filterTypes.COMPOSITION
                                ].includes(activeFilterType)
                                    ? 'single-step-right'
                                    : ''
                            }`}
                        >
                            {filteredList?.length > 0 && (
                                <div
                                    className={`item item1 d-flex justify-between items-center ${
                                        isAllCheckboxSelected()
                                            ? 'selected'
                                            : ''
                                    }`}
                                >
                                    <div className='d-flex items-center'>
                                        <input
                                            type='checkbox'
                                            name='categories'
                                            id='category_7'
                                            value=''
                                            onChange={onClickAllCheckbox}
                                            checked={isAllCheckboxSelected()}
                                        />
                                        <label
                                            htmlFor='category_7'
                                            className='regular-14 ml-2'
                                        >
                                            All
                                        </label>
                                    </div>
                                </div>
                            )}
                            {renderSecondSteps()}
                        </div>
                    )}
                    {activeFilterType === filterTypes.COMPOSITION &&
                        renderSecondStepsForComposition()}
                </div>
            </div>
            <div className='common-popup-footer flex items-center justify-end'>
                <button className='button gray mr-3' onClick={clearFilter}>
                    <span className='text-xs'>Clear filter</span>
                </button>
                <button className='button primary sm' onClick={onClickApply}>
                    Apply
                </button>
            </div>
        </div>
    )

    const onClickFilterType = async (value) => {
        setSearchTerm('')
        setFilterOptions(JSON.parse(JSON.stringify(filterStore)))
        setActiveFilterType(value)
        setShowFilter(true)
    }

    const onClickPriceRangeFilter = async (value) => {
        setActiveFilterType(value)
        setShowPriceRangeFilter(true)
    }

    const isFilterFieldSelected = (fieldName) => {
        return filterStore[fieldName]?.length > 0 ? 'active' : ''
    }

    const removeChip = (item) => {
        dispatch(
            FilterActionTypes[REMOVE_FILTER_FIELD]({
                name: item.fieldName,
                data: item.id ? item.id : item.fiberName
            })
        )
    }

    const getChipList = () => {
        return Object.entries(filterStore).reduce((chips, [item, values]) => {
            if (Array.isArray(values)) {
                const tmpValues = values.map((obj) => {
                    let label
                    if (item === 'composition') {
                        label = `${obj.fiberName}: ${obj.minPercentage}% - ${obj.maxPercentage}%`
                    } else if (item === 'priceRange') {
                        label = `Price: $${obj.minPrice} - $${obj.maxPrice}`
                    } else {
                        label = obj?.value
                    }

                    return { ...obj, fieldName: item, label }
                })
                return chips.concat(tmpValues)
            }

            if (values instanceof Object && values.value) {
                return [...chips, { ...values, fieldName: item }]
            }

            return chips
        }, [])
    }

    const renderChips = () => {
        const tmpChips = getChipList()
        return tmpChips?.map((item, index) => {
            return (
                <div
                    className='filter-chip'
                    key={`filter_chip_${index}`}
                    onClick={() => removeChip(item)}
                >
                    <span>{capitalizeFirstAndLowercaseRest(item.label)}</span>
                    <CloseIcon />
                </div>
            )
        })
    }

    const isShowClearAll = () => {
        let isShow = false

        for (let item in filterStore) {
            if (
                Array.isArray(filterStore[item]) &&
                filterStore[item].length > 0
            ) {
                isShow = true
            }
            if (
                filterStore[item]?.value === true ||
                filterStore[item]?.value === 'SOLD'
            ) {
                isShow = true
            }
        }
        return isShow
    }
    const onCloseModal = () => {
        setShowPriceRangeFilter(false)
        clearFilter()
    }

    return (
        <>
            <div
                className={`filter-top-header ${extraClass ? extraClass : ''}`}
            >
                <div className='chips-and-action'>
                    {filteredDataLength > 0 && (
                        <div className='chips'>
                            {requiredFields?.NEW_IN && (
                                <span
                                    className={`chip ${
                                        filterStore.isNew?.value ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                        dispatch(
                                            FilterActionTypes[
                                                ADD_FILTER_FIELDS
                                            ]({
                                                data: {
                                                    isNew: {
                                                        id: 'isNew',
                                                        value: !filterStore
                                                            .isNew?.value,
                                                        label: 'New In'
                                                    }
                                                }
                                            })
                                        )
                                        setLastActiveFilterType('New In')
                                    }}
                                >
                                    New in
                                </span>
                            )}
                            {requiredFields?.SOLD && (
                                <span
                                    className={`chip ${
                                        filterStore.availabilityStatus?.value
                                            ? 'active'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        dispatch(
                                            FilterActionTypes[
                                                ADD_FILTER_FIELDS
                                            ]({
                                                data: {
                                                    availabilityStatus: {
                                                        id: 'availabilityStatus',
                                                        value: 'SOLD',
                                                        label: 'Sold'
                                                    }
                                                }
                                            })
                                        )
                                        setLastActiveFilterType('Sold')
                                    }}
                                >
                                    Sold
                                </span>
                            )}
                            {requiredFields?.AGE_GROUP && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'ageGroups'
                                    )}`}
                                    onClick={() =>
                                        onClickFilterType(filterTypes.AGE_GROUP)
                                    }
                                >
                                    Age group
                                </span>
                            )}
                            {requiredFields?.RECENTLY_SHARED && (
                                <span
                                    className={`chip ${
                                        filterStore.recentlyShared?.value
                                            ? 'active'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        dispatch(
                                            FilterActionTypes[
                                                ADD_FILTER_FIELDS
                                            ]({
                                                data: {
                                                    recentlyShared: {
                                                        id: 'recently_shared',
                                                        value: !filterStore
                                                            .recentlyShared
                                                            ?.value,
                                                        label: 'Recently Shared'
                                                    }
                                                }
                                            })
                                        )
                                    }}
                                >
                                    Recently shared
                                </span>
                            )}
                            {requiredFields?.DESIGNERS_PICK && (
                                <span
                                    className={`chip ${
                                        filterStore.designersPick?.value
                                            ? 'active'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        dispatch(
                                            FilterActionTypes[
                                                ADD_FILTER_FIELDS
                                            ]({
                                                data: {
                                                    designersPick: {
                                                        id: 'designers_pick',
                                                        value: !filterStore
                                                            .designersPick
                                                            ?.value,
                                                        label: 'Designer’s Pick'
                                                    }
                                                }
                                            })
                                        )
                                        setLastActiveFilterType(
                                            'Designer’s Pick'
                                        )
                                    }}
                                >
                                    Designer’s pick
                                </span>
                            )}
                            {requiredFields?.DESIGN_LOOK && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'designLooks'
                                    )}`}
                                    onClick={() =>
                                        onClickFilterType(
                                            filterTypes.DESIGN_LOOK
                                        )
                                    }
                                >
                                    Design look
                                </span>
                            )}
                            {requiredFields?.CATEGORY && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'subCategories'
                                    )}`}
                                    onClick={() =>
                                        onClickFilterType(filterTypes.CATEGORY)
                                    }
                                >
                                    Category
                                </span>
                            )}
                            {requiredFields?.SEASON && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'seasons'
                                    )}`}
                                    onClick={() =>
                                        onClickFilterType(filterTypes.SEASON)
                                    }
                                >
                                    Season
                                </span>
                            )}
                            {requiredFields?.FABRIC && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'fabrics'
                                    )}`}
                                    onClick={() =>
                                        onClickFilterType(filterTypes.FABRIC)
                                    }
                                >
                                    Fabric
                                </span>
                            )}

                            {requiredFields?.DEPARTMENT && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'departments'
                                    )}`}
                                    onClick={() =>
                                        onClickFilterType(
                                            filterTypes.DEPARTMENT
                                        )
                                    }
                                >
                                    Department
                                </span>
                            )}
                            {requiredFields?.COMPOSITION && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'composition'
                                    )}`}
                                    onClick={() =>
                                        onClickFilterType(
                                            filterTypes.COMPOSITION
                                        )
                                    }
                                >
                                    Composition
                                </span>
                            )}
                            {requiredFields?.PRICE_RANGE && (
                                <span
                                    className={`chip ${isFilterFieldSelected(
                                        'priceRange'
                                    )}`}
                                    onClick={() =>
                                        onClickPriceRangeFilter(
                                            filterTypes.PRICE_RANGE
                                        )
                                    }
                                >
                                    Price Range
                                </span>
                            )}
                        </div>
                    )}
                    <div className='actions'>{children}</div>
                </div>
                {getChipList().length > 0 && (
                    <div className='filtered-chips'>
                        {renderChips()}
                        {isShowClearAll() && (
                            <button
                                className='button link mr-3'
                                onClick={() => {
                                    dispatch(
                                        FilterActionTypes[CLEAR_FILTER_FIELDS]()
                                    )
                                }}
                            >
                                <span className='text-xs'>Clear all</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
            {showFilter && (
                <ModalContainer
                    className='category-filter-modal'
                    onCloseModal={() => setShowFilter(false)}
                >
                    {renderFilterOptions()}
                </ModalContainer>
            )}
            {showPriceRangeFilter && (
                <PriceRangeModal
                    fieldName={getFieldName()}
                    setFilterOptions={setFilterOptions}
                    onClickApply={onClickApply}
                    onCloseModal={onCloseModal}
                />
            )}
        </>
    )
}

export default FilterComponent
